import './theme.scss';
import './reset.css';
import '../fonts/fonts.css';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { PageProps } from 'gatsby';
import SiteSearchProvider from '../components/site-search/site-search/provider';
import GridContextProvider from '../components/primitives/grid/context';
import { breakpoints } from '../components/primitives/tokens';
import LocatorProvider from '../contexts/franchise/locator-provider';
import { MicrositeProvider } from '../contexts/franchise/microsite-context';
import DniProvider from '../contexts/dni-provider';
import AnnouncementBar from '../components/header/announcement-bar';
import TemplateProvider from '../contexts/template-provider';
import MicrositeGtagProvider from '../contexts/franchise/microsite-gtag-provider';
import { Script as NavigationMenuScript } from '../components/primitives/navigation-menu';
import DebugProvider from '../components/primitives/debug/debug-provider';
import EventsProvider from '../contexts/events-provider';
import CtaProvider from '../contexts/franchise/cta-provider';
import Header from '../components/header/index';
import Footer from '../components/footer/index';
import TrackingScripts from '../components/tracking-scripts';
import LiveUpdateProvider from '../components/live-update/provider';
import useLiveDataTransform from '../components/live-update/use-live-data-transform';
import LiveUpdateWindow from '../components/live-update/live-update-window';
import useSiteMetadata from '../hooks/use-site-metadata';
import getCanonicalUrl from '../utils/get-canonical-url';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            cacheTime: 1000 * 60 * 60 * 72, // 72 hours
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            staleTime: Infinity,
        },
    },
});

/*
* 3rd party script for Cookie Compliance
* */
interface OneTrustWindow extends Window {
    OneTrust?: {
        initializeCookiePolicyHtml?: () => void
    };
}

// Utility to recursively find publishDetails.publishedAt
const findPublishedAt = (obj: unknown, context?: unknown): string | null => {
    if (context && typeof context === 'object') {
        if (
            'publishedAt' in context
        && context.publishedAt
        && typeof context.publishedAt === 'string'
        ) {
            return context.publishedAt;
        }
    }
    if (!obj || typeof obj !== 'object') return null;
    if (
        'publishDetails' in obj
      && obj.publishDetails
      && typeof obj.publishDetails === 'object'
      && 'publishedAt' in obj.publishDetails
      && obj.publishDetails.publishedAt
      && typeof obj.publishDetails.publishedAt === 'string'
    ) return obj.publishDetails.publishedAt;
    return Object.values(obj).reduce<string | null>((acc, value) => {
        if (acc) return acc;
        if (Array.isArray(value)) {
            return value.reduce<string | null>((found, item) => found || findPublishedAt(item), null);
        }
        if (typeof value === 'object') {
            return findPublishedAt(value) || acc;
        }
        return acc;
    }, null);
};

const Layout = (props: PageProps): JSX.Element => {
    const { children, location, data: originalData, pageContext, pageResources } = props;
    const { pathname } = location;
    const liveDataTransform = useLiveDataTransform(originalData as never);
    const { data } = liveDataTransform;
    const { siteUrl } = useSiteMetadata();

    // Extract publishedAt from data or pageContext
    const publishedAt = findPublishedAt(data, pageContext) || null;

    // Generate canonical URL for this page
    const canonicalUrl = getCanonicalUrl(pathname, siteUrl);
    React.useEffect(() => {
        const oneTrust = (window as OneTrustWindow).OneTrust;
        if (oneTrust?.initializeCookiePolicyHtml) {
            oneTrust.initializeCookiePolicyHtml();
        }
    }, [pathname]);

    return (
        <LiveUpdateProvider {...liveDataTransform}>
            <Helmet>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <EventsProvider>
                <TemplateProvider pageContext={pageContext} pageResources={pageResources}>
                    <GridContextProvider
                        breakpoints={{
                            dt: { query: breakpoints.dt, columns: 12, gap: 24 },
                            tb: { query: breakpoints.tb, columns: 12, gap: 24 },
                            mb: { query: breakpoints.mb, columns: 4, gap: 16 },
                        }}
                        maxWidth={1440}
                        desktopFirst
                    >
                        <DebugProvider
                            id={typeof pageContext === 'object' && pageContext && 'id' in pageContext && typeof pageContext.id === 'string' ? pageContext.id : ''}
                            pageProps={(() => {
                                const { children, ...rest } = props;
                                return rest;
                            })()}
                            publishedAt={publishedAt}
                        >
                            <QueryClientProvider client={queryClient}>
                                <LocatorProvider>
                                    <DniProvider>
                                        <MicrositeGtagProvider data={data}>
                                            <MicrositeProvider data={data}>
                                                <CtaProvider data={data}>
                                                    <SiteSearchProvider>
                                                        <AnnouncementBar />
                                                        <TrackingScripts />
                                                        <Header />
                                                        <main>
                                                            <LiveUpdateWindow>
                                                                {children}
                                                            </LiveUpdateWindow>
                                                        </main>
                                                        <Footer />
                                                    </SiteSearchProvider>
                                                </CtaProvider>
                                            </MicrositeProvider>
                                        </MicrositeGtagProvider>
                                    </DniProvider>
                                </LocatorProvider>
                            </QueryClientProvider>
                        </DebugProvider>
                    </GridContextProvider>
                </TemplateProvider>
                <NavigationMenuScript />
            </EventsProvider>
        </LiveUpdateProvider>
    );
};

export default Layout;
